/**
 * Capitalizes the first letter of each word in a string
 * @param text - The text to capitalize
 * @returns The capitalized text, or empty string if no text provided
 */
export const capitalize = (text?: string): string => {
  if (!text) {
    return '';
  }

  return text
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const sentenceCase = (text?: string): string => {
  if (!text) {
    return '';
  }

  return text
    .replace(/(\([^)]*\))|([^(]+)/g, (match, parenthesesContent) => {
      // Keep text inside parentheses unchanged
      if (parenthesesContent) {
        return parenthesesContent;
      }

      // Apply lowercase to text outside parentheses
      return match.toLowerCase();
    })
    .replace(/^./, (firstCharacter) => firstCharacter.toUpperCase());
};

/**
 * Converts a time string in HH:MM format to a human-readable format
 * @param val - Time string in HH:MM format
 * @returns Human-readable time format (e.g., "2 hr 30 min") or "0" for zero time
 */
export const changeToTimeString = (val?: string): string => {
  if (val) {
    const timeString = val;
    const [hours, minutes] = timeString.split(':').map(Number);

    if (hours === 0 && minutes === 0) {
      return '0';
    }

    let readableFormat = '';
    if (hours > 0) {
      readableFormat += `${hours} hr`;
    }

    if (minutes > 0) {
      if (readableFormat) {
        readableFormat += ' ';
      }
      readableFormat += `${minutes} min`;
    }

    return readableFormat;
  }
  return '';
};

/**
 * Truncates text to a specified length and adds ellipsis if needed
 * @param text - The text to truncate
 * @param maxLength - Maximum length before truncation (default: 12)
 * @returns Truncated text with ellipsis if longer than maxLength
 */
export const truncateByLength = (
  text?: string,
  maxLength: number = 12
): string => {
  if (!text) {
    return '';
  }
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

/**
 * Extracts initials from a full name (first and last name)
 * @param fullName - The full name to extract initials from
 * @returns First and last initials in uppercase (e.g., "JD" for "John Doe")
 */
export const getInitials = (fullName: string = ''): string => {
  const name = fullName || '';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '';

  const firstInitial = parts[0]?.charAt(0).toUpperCase() || '';
  const lastInitial =
    parts.length > 1 ? parts[parts.length - 1]?.charAt(0).toUpperCase() : '';

  return firstInitial + lastInitial;
};
